"use client";

import { Button } from "components/Elements/Button/Button";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import rehypeRaw from "rehype-raw";
import NewsPagination from "./NewsPagination";
import { useCallback, useTransition } from "react";
import { useRouter, usePathname } from "next/navigation";
import { ImSpinner2 } from "react-icons/im";

function PostGrid({ posts }: { posts: any[] }) {
  const first_post = posts[0];
  const remaining_posts = posts.slice(1, 10);

  if (!first_post) return null;

  return (
    <>
      <div className="flex flex-col w-[100%] mb-[50px]">
        <div className="h-[100%] w-[100%] relative flex flex-col lg:flex-row gap-[50px]">
          <div className="flex justify-start relative aspect-square overflow-hidden lg:overflow-visible">
            {first_post.x_featured_media_original ? (
              <img
                className="mx-auto w-[100%] h-[100%] lg:!max-w-[400px] lg:w-[400px] lg:h-[400px] hover:scale-[1.1] hover:opacity-75 aspect-square object-cover mb-[50px]"
                src={first_post.x_featured_media_original}
                alt={first_post.media_alt ?? ""}
              />
            ) : (
              <div className="mx-auto w-[100%] h-[100%] lg:!max-w-[400px] lg:w-[400px] lg:h-[400px] bg-gray-200 flex items-center justify-center aspect-square mb-[50px]">
                <span className="text-gray-500">No image available</span>
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center items-start px-[0px]">
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              className="w-[100%] lg:w-[75%] text-[20px] lg:text-[26px] font-semibold pt-[10px]"
            >
              {first_post.title.rendered}
            </ReactMarkdown>
            <ReactMarkdown
              className="w-[100%] lg:w-[75%] text-[16px] pt-[10px]"
              rehypePlugins={[rehypeRaw]}
            >
              {first_post.excerpt.rendered.replace(/\[av.*?]/g, "")}
            </ReactMarkdown>
            <Button 
              url={`/news/${first_post.slug}`}
              classes="flex justify-start"
              styles={{}}
              innerClasses=""
              aria={{}}
              ref={null}
              target="_self"
              rel=""
            >
              Read More
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-5">
        {remaining_posts.map((post) => (
          <article 
            key={post.id}
            className="overflow-hidden w-[100%] h-auto flex flex-col relative mb-[25px]"
          >
            <div className="flex flex-row justify-between mb-3">
              <p className="text-[14px] text-700">
                {new Intl.DateTimeFormat("en-gb").format(new Date(post.date))}
              </p>
              <ReactMarkdown
                className="font-bold text-[14px] text-700"
                rehypePlugins={[rehypeRaw]}
              >
                {post.x_categories?.split(",")?.[0] ?? ""}
              </ReactMarkdown>
            </div>
            <div className="aspect-[4/3] relative overflow-hidden mb-3">
              {post.x_featured_media_original ? (
                <img
                  src={post.x_featured_media_original}
                  className="h-[100%] w-[100%] hover:scale-[1.1] hover:opacity-75 transition-all duration-300 object-cover"
                  alt={post.media_alt ?? ""}
                />
              ) : (
                <div className="h-[100%] w-[100%] bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500">No image available</span>
                </div>
              )}
            </div>
            <div className="flex flex-col flex-grow">
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                className="text-[18px] font-semibold mb-2 line-clamp-2"
              >
                {post.title.rendered}
              </ReactMarkdown>
              <ReactMarkdown
                className="text-[14px] mb-3 line-clamp-2"
                rehypePlugins={[rehypeRaw]}
              >
                {post.excerpt.rendered.replace(/\[av.*?]/g, "")}
              </ReactMarkdown>
              <div className="mt-auto">
                <Button 
                  url={`/news/${post.slug}`}
                  classes="flex justify-start"
                  styles={{}}
                  innerClasses=""
                  aria={{}}
                  ref={null}
                  target="_self"
                  rel=""
                >
                  Read More
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function SearchPostResults({
  query,
  current_page,
  total_pages,
  posts,
}: {
  query: Record<string, string>;
  current_page: number;
  total_pages: number;
  posts: any[];
}) {
  const router = useRouter();
  const path = usePathname() ?? "/";
  const [isPending, startTransition] = useTransition();

  const goto_page = useCallback((page_num: number) => {
    const params = new URLSearchParams(query);
    if (page_num > 0) {
      params.set("p", page_num.toString());
    } else {
      params.delete("p");
    }
    startTransition(() => {
      router.push(`${path}?${params.toString()}`);
    });
  }, [path, query, router, startTransition]);

  if (!posts.length) {
    return (
      <div className="w-[85%] mx-auto mt-8">
        <h3 className="mt-[15px] mb-[40px] w-[100%] h-[auto] flex">
          No Posts Found
        </h3>
      </div>
    );
  }

  return (
    <div className="w-[85%] mx-auto mt-8 relative">
      <h3 className="mt-[15px] mb-[40px] w-[100%] h-[auto] flex">
        Post Results
      </h3>
      {isPending && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-20 bg-white/70 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm border border-neutral-200">
            <ImSpinner2 className="animate-spin" />
            <span>Loading articles...</span>
          </div>
        </div>
      )}
      <div className={isPending ? "opacity-50 transition-opacity duration-150" : "transition-opacity duration-150"}>
        <PostGrid posts={posts} />
      </div>
      <div className="w-full flex flex-row items-center justify-center mt-8">
        <NewsPagination
          current_page={current_page}
          pages={total_pages}
          goto_page={goto_page}
          isLoading={isPending}
        />
      </div>
    </div>
  );
}

export default SearchPostResults; 